import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { isDefined, isUndefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import {
    Body,
    EditHeader,
} from '../../common/components/LicensePlateDetailSlideIn.tsx';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import {
    LicensePlate,
    ServerDataState,
} from '../../common/state/LicensePlateState.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { ButtonSpecialState } from '../../ui/buttons/IconButton.tsx';
import { ConfirmationHeader, HalfSlideIn } from '../../ui/slidein/Slidein.tsx';
import * as PermitLicensePlateSlideinTexts from '../i18n/PermitLicensePlateSlideinTexts.ts';
import * as AddressState from '../state/AddressState.ts';
import * as LicensePlateState from '../state/LicensePlateState.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

const abortCreateProcess = (store: Store): string => {
    LicensePlateState.resetAllStates(store);
    return 'PermitLicensePlateSlideIn-abortCreateProcess';
};

export const addLicensePlateToNewPermit = (
    store: Store,
    lp: LicensePlate,
): string => {
    PermitCreateState.Config.set(store, (s: PermitCreateState.Config.State) => {
        const copy = s.licensePlates.slice();
        copy.push(lp);
        s.licensePlates = copy;
        return s;
    });
    LicensePlateState.resetAllStates(store);
    PermitCreateState.Calc.reset(store);
    AddressState.ServerList.reset(store);
    return 'PermitLicensePlateSlidein-addLicensePlateToNewPermit';
};

export const addLicensePlateToExistingPermit = (
    store: Store,
    lp: LicensePlate,
): string => {
    PermitEditState.Edit.set(store, (s: PermitEditState.Edit.State) => {
        // if the edit state is undefined, we copy over the plates from the server state
        const server = PermitEditState.Server.get(store).data;
        if (isUndefined(s.licensePlates) && isDefined(server)) {
            s.licensePlates = server.licensePlates.slice();
        }

        const editedLps = s.licensePlates;
        if (isDefined(editedLps)) {
            const copy = editedLps.slice();
            copy.push({
                id: lp.id,
                licensePlateNr: lp.licensePlateNr,
                type: lp.type,
                country: lp.country,
            });
            s.licensePlates = copy;
        }
        return s;
    });
    LicensePlateState.resetAllStates(store);
    PermitEditState.Validation.reset(store);
    AddressState.ServerList.reset(store);
    return 'PermitLicensePlateSlidein-addLicensePlateToNewPermit';
};

const getPermitType = (store: Store): Maybe<PermitTypeState.PermitType> => {
    const entity = LicensePlateState.Entity.get(store);
    if (entity.context === LicensePlateState.Entity.Context.NEW_PERMIT_ADD) {
        const permitTypeId = PermitCreateState.Config.get(store).permitTypeId;
        if (isDefined(permitTypeId)) {
            return PermitTypeState.getById(store, permitTypeId);
        }
    } else {
        const permit = PermitEditState.Server.get(store).data;
        if (isDefined(permit)) {
            return PermitTypeState.getById(store, permit.permitTypeId);
        }
    }
};

export function PermitLicensePlateSlidein() {
    const { storeState, update } = useStore(store => {
        const entity = LicensePlateState.Entity.get(store);
        return {
            entity,
            relevantPermitType: getPermitType(store),
            server: ServerDataState.get(
                store,
                entity.id ? { lpId: entity.id } : null,
            ),
            operatorData: OperatorDataState.get(store),
            currentLogin: CurrentOperatorLoginState.get(store),
        };
    });
    const { language } = useLanguage();

    function missingVignette(): boolean {
        const permitType = storeState.relevantPermitType;
        const serverData = storeState.server.data;
        if (permitType && serverData) {
            for (const allowedType of permitType.allowedLicensePlateTypes) {
                if (
                    allowedType.type === serverData.type &&
                    allowedType.needsVignette &&
                    serverData.vignettes.length === 0
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    function confirmDisabled(): boolean {
        return (
            isUndefined(storeState.relevantPermitType) ||
            isUndefined(storeState.server.data) ||
            missingVignette()
        );
    }

    function confirmButtonSpecialState(): Maybe<ButtonSpecialState> {
        if (storeState.entity.confirmPressedWhileError && confirmDisabled()) {
            return ButtonSpecialState.ERROR;
        }
        if (confirmDisabled()) {
            return ButtonSpecialState.DISABLED;
        }
        return null;
    }

    function renderHeader() {
        if (
            storeState.entity.context !== LicensePlateState.Entity.Context.EDIT
        ) {
            return (
                <ConfirmationHeader
                    language={language}
                    title={PermitLicensePlateSlideinTexts.permitLicensePlateSlideInTexts[
                        language
                    ].AddLicensePlateToPermit()}
                    onCancel={() => update(abortCreateProcess)}
                    confirmButtonSpecialState={confirmButtonSpecialState()}
                    onConfirm={() => {
                        const licensePlate = storeState.server.data;
                        if (confirmDisabled()) {
                            update(
                                LicensePlateState.Entity
                                    .setConfirmPressedWhilteError,
                            );
                        } else if (
                            storeState.entity.context ===
                            LicensePlateState.Entity.Context.NEW_PERMIT_ADD
                        ) {
                            update(store => {
                                if (licensePlate) {
                                    return addLicensePlateToNewPermit(
                                        store,
                                        licensePlate,
                                    );
                                }
                                return 'LicensePlate not valid';
                            });
                        } else if (
                            storeState.entity.context ===
                            LicensePlateState.Entity.Context.EXISTING_PERMIT_ADD
                        ) {
                            update(store => {
                                if (licensePlate) {
                                    return addLicensePlateToExistingPermit(
                                        store,
                                        licensePlate,
                                    );
                                }
                                return 'LicensePlate not valid';
                            });
                        } else {
                            return null;
                        }
                    }}
                />
            );
        } else {
            const lp = storeState.server.data;
            if (lp) {
                return (
                    <EditHeader
                        language={language}
                        lp={lp}
                        update={update}
                        onClose={() => update(LicensePlateState.resetAllStates)}
                        canEdit={
                            storeState.currentLogin.data
                                ? storeState.currentLogin.data.permissions
                                      .permitsWrite
                                : false
                        }
                    />
                );
            } else {
                return null;
            }
        }
    }

    return (
        <HalfSlideIn open={isDefined(storeState.entity.id)} outsideBody>
            {storeState.server.data && (
                <Body
                    lp={storeState.server.data}
                    language={language}
                    showTypeDescription={
                        (storeState.operatorData.data &&
                            storeState.operatorData.data.settings
                                .carTypeDescription) ||
                        false
                    }
                    showMissingVignetteError={
                        missingVignette() &&
                        storeState.entity.confirmPressedWhileError
                    }
                    update={update}
                    canEdit={
                        storeState.currentLogin.data
                            ? storeState.currentLogin.data.permissions
                                  .permitsWrite
                            : false
                    }
                />
            )}
            {renderHeader()}
        </HalfSlideIn>
    );
}
